import { HttpError } from '@forto-dev/ui-platform-http'
import {
  CreateProfileRequestAccessEnum,
  FeatureFlagResponse,
  ProfileResponse,
  SearchFeatureFlagsRequest,
  ShipmentBookmarkModel,
  ShipmentSavedFilterModel,
} from '@freighthub/user-profile-core-client'
import { useRootStore } from 'service/useRootStore'

import { SavedFiltersWithIds, SavedFilterWithId } from '../domain/SavedFilter'
import { ShipmentBookmarkWithId } from '../domain/ShipmentBookmark'

export class SaveFilterUniqueNameError extends Error {
  constructor(message: string) {
    super(message)
    Object.setPrototypeOf(this, SaveFilterUniqueNameError.prototype)
  }
}

const mapSavedFiltersResponse = (savedFiltersResponse: ProfileResponse): SavedFilterWithId => {
  return {
    savedFilterId: savedFiltersResponse.id,
    ...(savedFiltersResponse.value as ShipmentSavedFilterModel),
  }
}

const mapSavedFiltersResponses = (savedFiltersResponses: ProfileResponse[]): SavedFilterWithId[] => {
  return savedFiltersResponses?.map(mapSavedFiltersResponse) ?? []
}

const isUniqueNameError = (error: HttpError) =>
  error?.status === 400 && error?.message?.includes('value.name should be unique')

const handleSaveQueryError = (error: unknown) => {
  const httpError = error as HttpError
  if (isUniqueNameError(httpError)) {
    throw new SaveFilterUniqueNameError(httpError.message)
  }

  throw error
}

export type UseUserProfileCoreApi = {
  savedFilters: {
    getSavedFilters: () => Promise<SavedFiltersWithIds>
    createSavedFilter: (savedFilter: ShipmentSavedFilterModel) => Promise<string>
    deleteSavedFilter: (id: string) => Promise<void>
  }
  bookmarks: {
    getBookmarkedShipments: () => Promise<ShipmentBookmarkWithId[]>
    saveBookmark: (shipmentId: ShipmentBookmarkModel) => Promise<string>
    removeBookmark: (id: string) => Promise<void>
  }
  featureFlags: {
    getFeatureFlags: (searchFeatureFlags: SearchFeatureFlagsRequest) => Promise<FeatureFlagResponse[]>
  }
}

export const useUserProfileCoreApi = (): UseUserProfileCoreApi => {
  const { API } = useRootStore()

  return {
    savedFilters: {
      getSavedFilters: async (): Promise<SavedFiltersWithIds> => {
        const { owner, customer } = await API.userProfileCore.savedFilters.getSavedFilters()
        return {
          userSavedFilters: mapSavedFiltersResponses(owner),
          allCustomerSavedFilters: mapSavedFiltersResponses(customer),
        }
      },

      // @ts-expect-error TS(2366) FIXME: Function lacks ending return statement and return ... Remove this comment to see the full error message
      createSavedFilter: async (savedFilter: ShipmentSavedFilterModel): Promise<string> => {
        try {
          const { id: savedFilterId } = await API.userProfileCore.savedFilters.createSavedFilter({
            access: CreateProfileRequestAccessEnum.Customer,
            value: savedFilter,
          })
          return savedFilterId
        } catch (e) {
          handleSaveQueryError(e)
        }
      },

      deleteSavedFilter: async (id: string): Promise<void> => {
        return API.userProfileCore.savedFilters.deleteSavedFilter(id)
      },
    },

    bookmarks: {
      getBookmarkedShipments: async (): Promise<ShipmentBookmarkWithId[]> => {
        const { owner } = await API.userProfileCore.bookmarks.getBookmarkedShipments()
        return owner?.map((bookmark) => {
          const { shipmentId } = bookmark.value as ShipmentBookmarkModel
          return {
            bookmarkId: bookmark.id,
            shipmentId,
          }
        })
      },

      saveBookmark: async (shipmentId: ShipmentBookmarkModel): Promise<string> => {
        const { id: bookmarkId } = await API.userProfileCore.bookmarks.saveBookmark({
          access: CreateProfileRequestAccessEnum.Owner,
          value: shipmentId,
        })
        return bookmarkId
      },

      removeBookmark: async (id: string): Promise<void> => {
        return API.userProfileCore.bookmarks.removeBookmark(id)
      },
    },

    featureFlags: {
      getFeatureFlags: async (searchFeatureFlags: SearchFeatureFlagsRequest): Promise<FeatureFlagResponse[]> => {
        const { flags } = await API.userProfileCore.featureFlags.getFeatureFlags(searchFeatureFlags)
        return flags
      },
    },
  }
}
